import { useEffect, useRef, useState } from 'react';

export const QUOTE_NAVIGATION_HIGHLIGHT_COLOR = '#E6E6E6';

/** Fade from the highlight color back to the bubble's resting color. */
export const QUOTE_NAVIGATION_HIGHLIGHT_MS = 1500;

/**
 * If no scroll starts within this window, the target is already in place
 * and the highlight can begin.
 */
const SCROLL_IDLE_MS = 80;

/** Used when the browser does not emit `scrollend`. */
const SCROLL_END_FALLBACK_MS = 300;

/** Gives up waiting so a stuck scroll cannot leave the highlight pending. */
const SCROLL_SAFETY_MS = 2000;

/** Drops a highlight requested before its bubble is mounted. */
const PENDING_HIGHLIGHT_TTL_MS = 3000;

type HighlightListener = (messageId: string) => void;

let pendingMessageId: string | null = null;
let pendingTimer = 0;
const listeners = new Set<HighlightListener>();

/**
 * Asks the original message bubble to play its navigation highlight.
 * Listeners that are already mounted start immediately. If the bubble is not
 * mounted yet (pinned-messages view), the id stays pending until it mounts.
 */
export const requestQuoteNavigationHighlight = (messageId: string) => {
  if (!messageId) return;

  pendingMessageId = messageId;
  window.clearTimeout(pendingTimer);
  pendingTimer = window.setTimeout(() => {
    if (pendingMessageId === messageId) {
      pendingMessageId = null;
    }
  }, PENDING_HIGHLIGHT_TTL_MS);

  listeners.forEach((listener) => listener(messageId));
};

const subscribeQuoteNavigationHighlight = (listener: HighlightListener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Resolves once quote navigation scrolling has finished.
 * `scrollend` is the signal that the message reached its centered position.
 * If nothing scrolls, the idle timer treats the message as already in place.
 */
export const waitForQuoteScrollToSettle = (onSettled: () => void) => {
  let settled = false;
  let idleTimer = 0;
  let fallbackTimer = 0;
  let safetyTimer = 0;

  const cleanup = () => {
    document.removeEventListener('scroll', onScroll, true);
    document.removeEventListener('scrollend', onScrollEnd, true);
    window.clearTimeout(idleTimer);
    window.clearTimeout(fallbackTimer);
    window.clearTimeout(safetyTimer);
  };

  const finish = () => {
    if (settled) return;
    settled = true;
    cleanup();
    onSettled();
  };

  const onScroll = () => {
    window.clearTimeout(idleTimer);
    window.clearTimeout(fallbackTimer);
    fallbackTimer = window.setTimeout(finish, SCROLL_END_FALLBACK_MS);
  };

  const onScrollEnd = () => {
    finish();
  };

  document.addEventListener('scroll', onScroll, true);
  document.addEventListener('scrollend', onScrollEnd, true);

  idleTimer = window.setTimeout(finish, SCROLL_IDLE_MS);
  safetyTimer = window.setTimeout(finish, SCROLL_SAFETY_MS);

  return () => {
    settled = true;
    cleanup();
  };
};

/**
 * - `holding`: darkened while quote navigation scrolls the message to center.
 * - `fading`: fading back to the resting color once the message is centered.
 */
export type QuoteHighlightPhase = 'idle' | 'holding' | 'fading';

/**
 * Plays the temporary background highlight on the message that quote
 * navigation is scrolling to. Disabled in the pinned-messages view so the
 * pinned copy of the bubble is not highlighted before the conversation opens.
 */
export const useQuoteNavigationHighlight = (
  messageId: string,
  enabled: boolean
): QuoteHighlightPhase => {
  const [phase, setPhase] = useState<QuoteHighlightPhase>('idle');
  const fadeTimeoutRef = useRef<
    ReturnType<typeof window.setTimeout> | undefined
  >(undefined);

  useEffect(() => {
    if (!enabled || !messageId) return undefined;

    let cancelled = false;
    let cancelSettle: (() => void) | undefined;

    const startFade = () => {
      if (cancelled) return;
      if (pendingMessageId === messageId) {
        pendingMessageId = null;
        window.clearTimeout(pendingTimer);
      }

      setPhase('fading');
      fadeTimeoutRef.current = window.setTimeout(() => {
        if (!cancelled) setPhase('idle');
      }, QUOTE_NAVIGATION_HIGHLIGHT_MS);
    };

    const watch = () => {
      cancelSettle?.();
      window.clearTimeout(fadeTimeoutRef.current);
      setPhase('holding');
      cancelSettle = waitForQuoteScrollToSettle(startFade);
    };

    const unsubscribe = subscribeQuoteNavigationHighlight((id) => {
      if (id === messageId) watch();
    });

    if (pendingMessageId === messageId) {
      watch();
    }

    return () => {
      cancelled = true;
      cancelSettle?.();
      unsubscribe();
      window.clearTimeout(fadeTimeoutRef.current);
      setPhase('idle');
    };
  }, [enabled, messageId]);

  return phase;
};
